import { Box, Menu, Typography } from '@mui/material';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import type { PatientDetailDto } from '~/backend/connector-next-api/connector-next-schemas';
import { postTags } from '~/backend/connector-next-api/patientWritesClient';
import { LabelText } from '~/components/Fields/LabeledValue';
import { CheckboxMenuItem } from '~/components/Menu';
import {
    PERMISSION,
    useConnectorIdentity,
} from '~/hooks/auth/useConnectorIdentity';
import { useClinicalLists, usePatientWrite } from '../hooks';
import { offeredEntries, offeredWithHeld } from '../model';
import { QuietButton, TagChips } from '../shared';

/**
 * The patient's clinical tags under their label, with a menu to add or remove
 * one for a user who may change patients: the offered tags, then any retired
 * tag the patient holds. Each change is written at once, guarded by the
 * patient's version. Renders nothing with no tag to show.
 */
export default function PatientTagsField({
    patient,
}: {
    patient: PatientDetailDto;
}) {
    const intl = useIntl();
    const { data: lists } = useClinicalLists();
    const canModify = useConnectorIdentity().can(PERMISSION.patientModify);
    const tags = lists.tags;
    const tagIds = patient.tagIds ?? [];
    const {
        write: setTags,
        canWrite,
        isSaving,
        isError,
    } = usePatientWrite(patient, (ids: readonly string[], id, version) =>
        postTags(id, ids, version)
    );
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const options = offeredWithHeld(
        offeredEntries(tags ?? []),
        tagIds,
        tags,
        intl.formatMessage({ id: 'patientList.tags.unknown' })
    );
    const editable = canModify && options.length > 0;
    if (tagIds.length === 0 && !editable) {
        return null;
    }

    const toggle = (tagId: string) =>
        setTags(
            tagIds.includes(tagId)
                ? tagIds.filter((each) => each !== tagId)
                : [...tagIds, tagId]
        );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <LabelText>
                {intl.formatMessage({ id: 'patientList.tags.label' })}
            </LabelText>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.75,
                }}
            >
                <TagChips tagIds={tagIds} tags={tags} />
                {editable && (
                    <QuietButton
                        disabled={isSaving || !canWrite}
                        onClick={(event) => setAnchor(event.currentTarget)}
                        aria-haspopup="menu"
                        aria-expanded={anchor !== null}
                    >
                        <Plus size={12} aria-hidden />
                        {intl.formatMessage({ id: 'patientList.tags.edit' })}
                    </QuietButton>
                )}
            </Box>
            {isError && (
                <Typography
                    variant="caption"
                    color="error"
                    role="alert"
                    component="div"
                >
                    {intl.formatMessage({ id: 'patientList.tags.saveFailed' })}
                </Typography>
            )}
            <Menu
                open={anchor !== null}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
            >
                {options.map(({ value, label }) => (
                    <CheckboxMenuItem
                        key={value}
                        checked={tagIds.includes(value)}
                        label={label}
                        disabled={isSaving}
                        onClick={() => toggle(value)}
                    />
                ))}
            </Menu>
        </Box>
    );
}
